import { DiscoveryStatusBadge } from '@/components/discovery/DiscoveryStatusBadge';
import { GraphConfidenceCard } from '@/components/discovery/GraphConfidenceCard';
import { MetricsCard } from '@/components/discovery/MetricsCard';
import { SuggestedKnowledgeCard } from '@/components/discovery/SuggestedKnowledgeCard';
import { SuggestedLabelsCard } from '@/components/discovery/SuggestedLabelsCard';
import { useKnowledge } from '@/hooks/useKnowledge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useCancelDiscovery, useDiscovery } from '@/hooks/useDiscoveries';
import { extractApiError } from '@/lib/api';
import { downloadDiscoveryResult, isTerminal } from '@/lib/discoveries';
import { friendlyError } from '@/lib/error-messages';
import { AlertCircle, ArrowLeft, Download, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

// xyflow + elkjs together are ~200 KB — keep them out of the bundle
// for every non-detail page via React.lazy. Skeleton shows while the
// chunk + the elk layout settle.
const GraphView = React.lazy(() => import('@/components/discovery/GraphView'));

// Map perfection_runner phase tags to human-readable copy. Keep these in
// sync with PHASE_* constants in backend/app/services/perfection_runner.py.
// Keep in sync with PHASE_* constants in
// backend/app/services/perfection_runner.py.
const PHASE_COPY: Record<string, string> = {
  starting: 'Starting up the worker…',
  ges_done: 'Score-based search done — running constraint-based pass.',
  pc_done: 'PC algorithm done — measuring edge stability.',
  bootstrap_done: 'Bootstrap stability done — applying domain knowledge.',
  // Discrete (MMHC) path: one tick per bootstrap resample. Big categorical
  // networks (20-40 vars) spend most of the run here, so this keeps the bar
  // moving instead of frozen at 0%.
  bootstrap_discrete: 'Measuring edge stability across resamples…',
  constraints_done: 'Domain constraints applied — voting across methods.',
  consensus_done: 'Multi-method consensus done — finding missing edges.',
  residual_done: 'Residual analysis done — finalising graph.',
  done: 'Wrapping up.',
  // Executor post-processing — algorithm is done but the two Claude calls
  // (narrative + suggestion) take 10-30s on a busy graph. Showing real
  // labels keeps the bar honest instead of frozen at 100%.
  generating_narrative: 'Generating the plain-English insights…',
  generating_suggestion: 'Asking the agent to propose the next refinement…',
};
function progressPhaseLabel(phase: string | null | undefined): string {
  if (!phase) return 'Waiting for a worker — auto-refreshing every 2 seconds.';
  return PHASE_COPY[phase] ?? 'Working…';
}

// Title shown in the card header while the job is queued.
// 0 = "Next up" (less alarming than "0 ahead of you"); else the count.
function queueTitle(pos: number | null | undefined): string {
  if (pos == null) return 'Queued';
  if (pos === 0) return 'Up next';
  if (pos === 1) return '1 job ahead of you';
  return `${pos} jobs ahead of you`;
}

function queueSubtitle(pos: number | null | undefined): string {
  if (pos == null || pos === 0) {
    return 'A worker will pick this up momentarily.';
  }
  return 'We process one discovery per worker — your run starts as the queue clears.';
}

// Smoothly interpolate the server-reported progress so the bar keeps moving
// between polls (which arrive every 2-10s and otherwise leave it frozen for
// seconds at a time). Rules:
// - Forward-only: never moves backwards if the server reports a lower value.
// - Snap up to a fresh server value (CSS easing on the bar handles the visual
//   smoothness).
// - Optimistic creep: between polls advance ~2.5%/s toward an upper bound of
//   (latestServer + 8), capped at 95, so the bar feels alive but never claims
//   the run is nearly done before it actually is.
function useSmoothedProgress(serverValue: number): number {
  const [smooth, setSmooth] = React.useState(serverValue);
  React.useEffect(() => {
    setSmooth((cur) => Math.max(cur, serverValue));
  }, [serverValue]);
  React.useEffect(() => {
    if (smooth >= 99 || serverValue >= 100) return;
    const id = setInterval(() => {
      setSmooth((cur) => {
        const cap = Math.min(serverValue + 8, 95);
        if (cur >= cap) return cur;
        return Math.min(cur + 0.5, cap);
      });
    }, 200);
    return () => clearInterval(id);
  }, [serverValue, smooth]);
  return smooth;
}

export default function DiscoveryDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: discovery, isLoading, isError } = useDiscovery(id);
  const cancel = useCancelDiscovery();
  // Smoothly-interpolated progress for the bar + percent label. MUST run
  // every render (Rules of Hooks) — put it BEFORE the early-returns below,
  // and feed it 0 when discovery isn't loaded yet.
  //
  // The backend bumps progress to 96-98 during the narrative/suggestion
  // LLM round-trips (post-algorithm). Cap visual at 99 until status is
  // actually 'succeeded' so the bar never sits at 100% lying about a run
  // that's still finishing.
  const rawPct = discovery?.progress_pct ?? 0;
  const cappedPct =
    discovery?.status !== 'succeeded' && rawPct >= 100 ? 99 : rawPct;
  const smoothedPct = useSmoothedProgress(cappedPct);

  function onCancel() {
    if (!id) return;
    cancel.mutate(id, {
      onSuccess: () => toast.success('Discovery canceled.'),
      onError: (err) => {
        const apiErr = extractApiError(err);
        toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to cancel.'));
      },
    });
  }

  const [downloading, setDownloading] = React.useState(false);
  async function onDownload() {
    if (!id) return;
    setDownloading(true);
    try {
      await downloadDiscoveryResult(id);
    } catch (err) {
      const apiErr = extractApiError(err);
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Download failed.'));
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-24" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (isError || !discovery) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Discovery not found</CardTitle>
          <CardDescription>This run may have been deleted or it never existed.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/discoveries">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to discoveries
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const cancelable = !isTerminal(discovery.status);
  const failed = discovery.status === 'failed' || discovery.status === 'timeout';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="font-mono text-lg font-semibold tracking-tight">{discovery.id}</h1>
            <DiscoveryStatusBadge status={discovery.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            Dataset:{' '}
            <Link to="/datasets" className="font-mono hover:underline">
              {discovery.dataset_id}
            </Link>{' '}
            · Algorithm: {discovery.algorithm_version} · Seed:{' '}
            {String(discovery.params.seed ?? '—')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/discoveries">
              <ArrowLeft className="mr-1 h-4 w-4" /> All discoveries
            </Link>
          </Button>
          {cancelable ? (
            <Button variant="destructive" onClick={onCancel} disabled={cancel.isPending}>
              {cancel.isPending ? 'Canceling…' : 'Cancel'}
            </Button>
          ) : null}
          {failed || discovery.status === 'canceled' ? (
            <Button onClick={() => navigate(`/discoveries/new?dataset_id=${discovery.dataset_id}`)}>
              <RotateCcw className="mr-1 h-4 w-4" /> Try again
            </Button>
          ) : null}
        </div>
      </div>

      {discovery.status === 'queued' || discovery.status === 'running' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {discovery.status === 'queued' ? queueTitle(discovery.queue_position) : 'Running PERFECTION'}
            </CardTitle>
            <CardDescription>
              {discovery.status === 'queued'
                ? queueSubtitle(discovery.queue_position)
                : progressPhaseLabel(discovery.progress_phase)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress
              value={smoothedPct}
              indeterminate={(discovery.progress_pct ?? 0) === 0}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {(discovery.progress_pct ?? 0) === 0 ? 'Starting…' : `${Math.round(smoothedPct)}%`}
              </span>
              <span>{discovery.status === 'running' ? 'Auto-refreshing every 2s' : 'Auto-refreshing every 10s'}</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <MetricsCard discovery={discovery} />
          </div>
          <GraphConfidenceCard discovery={discovery} />
        </div>
      )}

      <AppliedKnowledgeLine knowledgeId={discovery.knowledge_id} />

      {discovery.status === 'succeeded' &&
      discovery.suggested_knowledge &&
      discovery.result?.nodes ? (
        <SuggestedKnowledgeCard
          discovery={discovery}
          resultNodes={discovery.result.nodes}
        />
      ) : null}

      {discovery.status === 'succeeded' &&
      discovery.suggested_labels &&
      discovery.suggested_labels.length > 0 &&
      discovery.result?.nodes ? (
        <SuggestedLabelsCard
          discovery={discovery}
          resultNodes={discovery.result.nodes}
        />
      ) : null}

      {failed ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {friendlyError(discovery.error_code ?? undefined, 'Discovery failed')}
          </AlertTitle>
          <AlertDescription>
            {discovery.error_message ?? 'No additional detail returned.'}
            {discovery.error_code ? (
              <span className="mt-1 block font-mono text-xs text-muted-foreground">
                {discovery.error_code}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {discovery.status === 'succeeded' ? (
        discovery.result ? (
          <React.Suspense
            fallback={
              <div className="flex h-[600px] items-center justify-center rounded-md border text-sm text-muted-foreground">
                Loading graph…
              </div>
            }
          >
            <GraphView discovery={discovery} />
          </React.Suspense>
        ) : discovery.result_storage_key ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Result too large to display</CardTitle>
              <CardDescription>
                Your discovery succeeded but the result exceeds the inline display limit
                (1&nbsp;MB). Download the JSON to inspect it locally or import it into another tool.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={onDownload} disabled={downloading}>
                <Download className="mr-1 h-4 w-4" />
                {downloading ? 'Preparing…' : 'Download result'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Result missing</CardTitle>
              <CardDescription>
                The worker reported success but no result is attached.
              </CardDescription>
            </CardHeader>
          </Card>
        )
      ) : null}
    </div>
  );
}

// Inline pill above the graph telling the user which knowledge_object was
// applied to this run. Hidden when no knowledge attached.
function AppliedKnowledgeLine({ knowledgeId }: { knowledgeId: string | null | undefined }) {
  const q = useKnowledge(knowledgeId ?? undefined);
  if (!knowledgeId) return null;
  const name = q.data?.name;
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
      <span className="text-muted-foreground">Applied knowledge:</span>
      <Link
        to={`/knowledge/${knowledgeId}/edit`}
        className="font-medium hover:underline"
      >
        {name ?? knowledgeId}
      </Link>
    </div>
  );
}
